import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onConnected: (tokenId: string, owner: string, repo: string) => void;
}

const GitHubConnectModal: React.FC<Props> = ({ open, onClose, onConnected }) => {
  const [stage, setStage] = useState<'idle' | 'code' | 'poll' | 'select' | 'create' | 'error'>(
    'idle',
  );
  const [device, setDevice] = useState<{
    user_code?: string;
    verification_uri?: string;
    device_code?: string;
    interval?: number;
    userCode?: string;
    verificationUri?: string;
    deviceCode?: string;
    expiresIn?: number;
  } | null>(null);
  const [tokenId, setTokenId] = useState<string>('');
  const [repos, setRepos] = useState<any[]>([]);
  const [newRepo, setNewRepo] = useState<{ name: string; private: boolean }>({
    name: '',
    private: true,
  });
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setStage('idle');
    setDevice(null);
    setRepos([]);
    setError('');
  }, [open]);

  const startDeviceFlow = async () => {
    setError('');
    try {
      const res = await api.post('/api/integrations/github/device/start');
      const data = res.data;
      setDevice(data);
      setStage('code');
    } catch (e: any) {
      setStage('error');
      setError(e?.response?.data?.error || e?.message || 'Failed to start GitHub device flow');
    }
  };

  const pollForToken = async () => {
    if (!device) return;
    setStage('poll');
    try {
      const body = { deviceCode: device.device_code || device.deviceCode };
      const res = await api.post('/api/integrations/github/device/poll', body);
      const data = res.data;
      if (data.status === 'pending') {
        setStage('error');
        setError('Authorization pending. Please authorize and try again.');
        return;
      }
      if (data.status !== 'success' || !data.tokenId) {
        setStage('error');
        setError('Authorization failed');
        return;
      }
      setTokenId(data.tokenId);
      await loadRepos();
    } catch (e: any) {
      setStage('error');
      setError(e?.response?.data?.error || e?.message || 'Failed to poll for token');
    }
  };

  const loadRepos = async () => {
    try {
      const res = await api.get('/api/integrations/github/repos');
      setRepos(res.data);
      setStage('select');
    } catch (e: any) {
      setStage('error');
      setError(e?.response?.data?.error || e?.message || 'Failed to load repositories');
    }
  };

  const createRepository = async () => {
    try {
      const res = await api.post('/api/integrations/github/repos', {
        name: newRepo.name,
        isPrivate: newRepo.private,
      });
      const repo = res.data;
      onConnected(tokenId, repo.owner, repo.name);
      onClose();
    } catch (e: any) {
      setStage('error');
      setError(e?.response?.data?.error || e?.message || 'Failed to create repository');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">Connect GitHub</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
            ✕
          </button>
        </div>

        {stage === 'idle' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Connect your GitHub account to select or create a repository for storing backups.
            </p>
            <button
              onClick={startDeviceFlow}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg"
            >
              Connect GitHub
            </button>
          </div>
        )}

        {stage === 'code' && device && (
          <div className="space-y-3">
            <p className="text-sm">
              Open{' '}
              <a
                href={(device.verification_uri || device.verificationUri) as string}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600"
              >
                {(device.verification_uri || device.verificationUri) as string}
              </a>{' '}
              and enter this code:
            </p>
            <div className="text-2xl font-mono tracking-widest bg-slate-100 dark:bg-slate-700 p-3 rounded text-center">
              {(device.user_code || device.userCode) as string}
            </div>
            <button onClick={pollForToken} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
              I authorized, continue
            </button>
          </div>
        )}

        {stage === 'poll' && (
          <div className="text-sm text-slate-600">Waiting for authorization...</div>
        )}

        {stage === 'select' && (
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Choose an existing repository</h4>
              <div className="max-h-48 overflow-auto border rounded divide-y">
                {repos.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">No repositories found.</div>
                ) : (
                  repos.map((r: any, idx: number) => {
                    const key =
                      r.id ||
                      r.full_name ||
                      `${r.owner?.login || r.owner || 'owner'}-${r.name}-${idx}`;
                    const fullName = r.full_name || `${r.owner?.login || r.owner}/${r.name}`;
                    return (
                      <div key={key} className="flex items-center justify-between px-3 py-2">
                        <div className="text-sm truncate" title={fullName}>
                          {fullName}
                        </div>
                        <button
                          className="text-blue-600 text-sm hover:underline"
                          onClick={async () => {
                            onConnected(tokenId, r.owner?.login || r.owner, r.name);
                            onClose();
                          }}
                        >
                          Use
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Or create a new repository</h4>
              <div className="flex items-center gap-2">
                <input
                  className="flex-1 px-3 py-2 border rounded"
                  placeholder="repo-name"
                  value={newRepo.name}
                  onChange={(e) => setNewRepo({ ...newRepo, name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newRepo.name.trim()) createRepository();
                  }}
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newRepo.private}
                    onChange={(e) => setNewRepo({ ...newRepo, private: e.target.checked })}
                  />
                  private
                </label>
                <button
                  onClick={createRepository}
                  disabled={!newRepo.name.trim()}
                  className={`px-3 py-2 rounded text-white ${newRepo.name.trim() ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-400 cursor-not-allowed'}`}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {stage === 'error' && (
          <div className="text-sm text-red-600">{error || 'Something went wrong'}</div>
        )}
      </div>
    </div>
  );
};

export default GitHubConnectModal;
